import { Container, Link } from '@cluey/cluey-components';
import { first, isNil } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, generatePath, useHistory, useLocation, useParams } from 'react-router';
import { z } from 'zod';

import { fetchEnrolment } from '../../../../actions/hub/hubEnrolmentManagementActions';
import { api } from '../../../../api';
import withHeadTag from '../../../../common/HeadComponent';
import { SkeletonLoader } from '../../../../components/SkeletonLoader';
import { CodeCampPromo } from '../../../../components/hub/home/CodeCampPromo';
import { HubSessionsListItem } from '../../../../components/hub/sessions/HubSessionsListItem';
import { HubSessionListSkeletonItem } from '../../../../components/hub/sessions/HubSessionsListItem/HubSessionListSkeletonItem';
import { SpecialNeedsData } from '../../../../components/hub/student/SpecialNeeds';
import { StudentInformationFormData } from '../../../../components/hub/student/StudentInformationForm';
import { getFeatureFlag, getFeatureFlagPayload } from '../../../../selectors/featureFlagsSelector';
import { ENROLMENT_STATUS, FEATURE_FLAG_NAMES } from '../../../../types/hubEnums';
import { InitialState } from '../../../../types/hubTypes';
import { isEnrolmentPausedCheck, isGroupSessionCheck } from '../../../../util/helpers';
import useScrollToTop from '../../../../util/hooks/useScrollToTop';
import {
  PATH_HUB_CR_ADD_ENROLMENT,
  PATH_HUB_ENROLMENT_MANAGE_PAUSE,
  PATH_HUB_HOME,
  PATH_HUB_MS_SESSION_TIMES,
  PATH_HUB_STUDENTS,
} from '../../../../util/pagePath';
import { PATH_HUB_UPCOMING_SESSIONS } from '../../../../util/pagePath';
import { useSetPageHeader } from '../../../../v2/app-shell/usePageHeader';
import { useGetBulkSessionPolicy } from '../../../../v2/common/hooks/useGetBulkSessionPolicy';
import { LearningProfile } from './LearningProfile';

type CodeCampPromo = {
  startDate: string;
  endDate: string;
  yearLevels: string[];
};

const StudentProfile = () => {
  useScrollToTop();
  const { studentSfid } = useParams<{ studentSfid: string }>();
  const dispatch = useDispatch();
  const { data: students, isLoading: loadingStudents } = api.student.all.useQuery();
  const { data: accountDetails, isLoading: loadingAccountDetails } = api.account.details.useQuery();
  const { data: studentEnrolments, isLoading: loadingEnrolments } = api.enrolment.all.useQuery('active', {
    select: (data) => data.filter((enrolment) => enrolment.studentSfid === studentSfid),
  });
  const { data: sessions = [], isLoading: isLoadingSessions } = api.session.all.useQuery('upcoming');
  const history = useHistory();
  const location = useLocation<{ fromAddStudentSuccess?: boolean }>();

  const student = students?.find((student) => student.studentId === studentSfid);

  // Drive the app-shell header: a back button + "{firstName}'s profile", replacing
  // the static "Students" header on this sub-route. Empty label while loading so
  // we don't flash "undefined's profile".
  // If we arrived here from the add-student success screen, the default back
  // button (history.goBack()) would return to that one-time confirmation screen,
  // so send it to the students list instead.
  useSetPageHeader({
    label: student ? `${student.firstName}'s profile` : '',
    showBackButton: true,
    onBackClick: location.state?.fromAddStudentSuccess ? () => history.push(PATH_HUB_STUDENTS) : undefined,
  });

  const everyEnrolmentNextSession = useMemo(() => {
    return studentEnrolments
      ?.map((enrolment) => {
        const firstSession = first(enrolment.sessions);
        return sessions.find((sesh) => sesh.studentSessionId === firstSession?.studentSessionId);
      })
      .filter((sesh) => !isNil(sesh));
  }, [studentEnrolments, sessions]);

  const { data: sessionPolicies, isLoading: isLoadingSessionPolicies } = useGetBulkSessionPolicy({
    sessionIds: everyEnrolmentNextSession.map((session) => session.studentSessionId),
    enabled: !!everyEnrolmentNextSession,
  });

  const codeCampPromoFlagEnabled = useSelector((state: InitialState) =>
    getFeatureFlag(state, FEATURE_FLAG_NAMES.SUMMER_PAUSE_CODE_CAMP_PROMO)
  );
  const codeCampPromo = useSelector((state: InitialState) =>
    getFeatureFlagPayload(state, FEATURE_FLAG_NAMES.SUMMER_PAUSE_CODE_CAMP_PROMO)
  ) as CodeCampPromo;

  const isDuringCodeCampPromo = moment().isBetween(codeCampPromo?.startDate, codeCampPromo?.endDate, 'day', '[]');
  const codeCampPromoEligibleEnrolments = studentEnrolments?.filter((enrolment) => {
    const isYearLevelEligible = codeCampPromo?.yearLevels.includes(enrolment.yearLevel);
    return isYearLevelEligible;
  });

  const isAU = accountDetails?.country === 'AU';
  const showCodeCampPromo =
    codeCampPromoFlagEnabled && isDuringCodeCampPromo && codeCampPromoEligibleEnrolments?.length > 0 && isAU;

  if (!loadingStudents && !student) {
    return <Redirect to={PATH_HUB_HOME} />;
  }

  const onEnrolNow = () => {
    const { firstName, lastName, yearLevel, location, studentId } = student ?? {};
    history.push(PATH_HUB_CR_ADD_ENROLMENT, {
      firstName,
      lastName,
      yearLevel: isNil(yearLevel) ? undefined : yearLevel,
      location: isNil(location) ? undefined : location,
      studentId,
    });
  };

  const hasUpcomingSession = everyEnrolmentNextSession?.length > 0;
  const cancelRequestedEnrolments =
    studentEnrolments?.filter((enrolment) => enrolment.enrolmentStatus === ENROLMENT_STATUS.CANCEL_REQUESTED) || [];

  return (
    <div className="p-5 pt-0 md:p-10 md:pt-0">
      <div className="mt-4 flex flex-col gap-y-4 rounded-lg border border-grey-3 bg-white px-4 py-4 shadow-md md:px-20 md:py-14">
        <SkeletonLoader className="h-4 w-14" loading={isLoadingSessions}>
          <h4 className="text-2xl font-bold">Upcoming sessions</h4>
        </SkeletonLoader>
        <div className="flex flex-col items-start justify-between text-sm md:flex-row md:items-center">
          <SkeletonLoader className="h-5 w-32" loading={loadingAccountDetails || isLoadingSessions}>
            <span>Times are in your account timezone ({accountDetails?.timezone})</span>
          </SkeletonLoader>
          <SkeletonLoader className="h-5 w-14" loading={loadingAccountDetails || isLoadingSessions}>
            {hasUpcomingSession && (
              <Link className="link mt-2.5 text-base md:mt-0" to={PATH_HUB_UPCOMING_SESSIONS}>
                View all sessions
              </Link>
            )}
          </SkeletonLoader>
        </div>
        {loadingEnrolments || isLoadingSessions || isLoadingSessionPolicies ? (
          <HubSessionListSkeletonItem />
        ) : (
          <div className="flex flex-col gap-y-4">
            {hasUpcomingSession &&
              everyEnrolmentNextSession.map((session) => (
                <HubSessionsListItem
                  key={session.studentSessionId}
                  session={session}
                  timezone={accountDetails.timezone}
                  policy={sessionPolicies[session.studentSessionId]}
                />
              ))}
            {cancelRequestedEnrolments.map((enrolment) => {
              const enrolmentId = enrolment.enrolmentId || '';
              const isPaused = isEnrolmentPausedCheck({ enrolmentStatus: enrolment.enrolmentStatus });
              const isGroup = isGroupSessionCheck(enrolment.type);
              const sessionsAndScheduleOptionsPath = !!enrolmentId
                ? generatePath(PATH_HUB_MS_SESSION_TIMES, { enrolmentId })
                : '';
              const managePausePath = `${PATH_HUB_ENROLMENT_MANAGE_PAUSE}?token=${enrolmentId}`;
              const clearPreviouslySelectedEnrolment = () => {
                dispatch(fetchEnrolment({ enrolmentId })); // see: components/hub/enrolments/EnrolmentCard/EnrolmentCard.tsx line 88 for explanation
              };
              const manageEnrolment = () => {
                if (isPaused) {
                  clearPreviouslySelectedEnrolment();
                }
                history.push(isPaused && !isGroup ? managePausePath : sessionsAndScheduleOptionsPath);
              };

              return (
                <div className="flex flex-col items-start justify-between rounded-lg border border-dashed border-slate-3 px-7 py-5 md:flex-row md:items-center">
                  <div className="flex flex-row flex-wrap items-center gap-2">
                    <div className="text-base font-bold">{student?.firstName}</div>
                    <div className="h-3 w-0.5 rounded-full bg-slate-4 opacity-40"></div>
                    <p>
                      {enrolment.yearLevel} {enrolment.subject} {enrolment.tutor && `(with ${enrolment.tutor})`}
                    </p>
                    <p className="font-bold">Cancellation requested</p>
                  </div>
                  <button className="mt-2 self-start text-link md:mt-0" onClick={manageEnrolment}>
                    Manage
                  </button>
                </div>
              );
            })}
            {!hasUpcomingSession && cancelRequestedEnrolments.length < 1 && (
              <div className="flex flex-col items-start justify-between rounded-lg border border-dashed border-slate-3 px-7 py-5 md:flex-row md:items-center">
                <div>
                  <span className="text-base font-bold">{student?.firstName}</span>
                  <span> &nbsp;|&nbsp; No active enrolment</span>
                </div>
                <button className="link" onClick={onEnrolNow}>
                  + Enrol now
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showCodeCampPromo && (
        <Container className="my-4">
          <CodeCampPromo isStudentProfilePage />
        </Container>
      )}
      <div className="h-10" />
      <LearningProfile loading={loadingStudents} student={student} />
    </div>
  );
};

export const StudentProfileData = z.intersection(StudentInformationFormData, SpecialNeedsData);
export type StudentProfileData = z.infer<typeof StudentProfileData>;

const StudentProfileWithHeadTag = withHeadTag(StudentProfile);

export { StudentProfileWithHeadTag as StudentProfile };
